import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import { useSessionStore } from '../../store/sessionStore';
import CongratsModal from '../CongratsModal';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { useLocation } from 'react-router-dom';
import {
  profileCompletionMessage,
  resolveScreeningReadiness,
} from '../../utils/profileCompletionScore';

const PROFILE_COMPLETE_BANNER_DISMISS_KEY = 'ss-profile-complete-banner-dismissed';

function profileCompleteBannerDismissKey(userId: number | string): string {
  return `${PROFILE_COMPLETE_BANNER_DISMISS_KEY}:${userId}`;
}

function isProfileCompleteBannerDismissed(userId: number | string | undefined): boolean {
  if (userId == null || userId === '') return false;
  try {
    return localStorage.getItem(profileCompleteBannerDismissKey(userId)) === '1';
  } catch {
    return false;
  }
}

function dismissProfileCompleteBanner(userId: number | string): void {
  try {
    localStorage.setItem(profileCompleteBannerDismissKey(userId), '1');
  } catch {
    // ignore quota / private mode
  }
}

const LEGACY_SESSION_DISMISS_PREFIX = 'ss-onboarding-progress-session-dismissed';

function clearLegacySessionDismissFlags(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(LEGACY_SESSION_DISMISS_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore quota / private mode
  }
}

interface OnboardingProgressProps {
  customMessage?: string;
  registration?: boolean;
}

const OnboardingProgress: React.FC<OnboardingProgressProps> = ({ customMessage }) => {
  const location = useLocation();
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string | null>(null);
  const [dismissedThisView, setDismissedThisView] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [showCongrats, setShowCongrats] = useState<boolean>(false);
  const [userData, setUserData] = useState<Record<string, unknown> | null>(null);
  const hasShownRef = useRef(false);
  const hasInitialLoadedRef = useRef(false);

  const applyProgressFromSession = useCallback(
    (
      user: Record<string, unknown> | null | undefined,
      prompts: unknown[],
      portfolio: Record<string, unknown> | null | undefined
    ) => {
      const result = resolveScreeningReadiness(user, prompts, portfolio);
      setProgress(result.percent);
      setMessage(customMessage || profileCompletionMessage(result));
      return result;
    },
    [customMessage]
  );

  const refreshProgress = useCallback(
    async (force = false) => {
      const isInitial = !hasInitialLoadedRef.current;
      try {
        if (isInitial) {
          setLoading(true);
        }
        const sessionStore = useSessionStore.getState();
        await sessionStore.refreshUser({ force: true });
        const { user, prompts, portfolio } = await sessionStore.bootstrapSession({ force });

        setUserData(user as Record<string, unknown>);

        if (location.pathname === '/create-prompt') {
          return;
        }

        applyProgressFromSession(
          user as Record<string, unknown>,
          prompts || [],
          portfolio as Record<string, unknown> | null
        );
      } catch (err) {
        console.error('Error calculating onboarding progress:', err);
      } finally {
        if (isInitial) {
          hasInitialLoadedRef.current = true;
          setLoading(false);
        }
      }
    },
    [applyProgressFromSession, location.pathname]
  );

  useEffect(() => {
    clearLegacySessionDismissFlags();
    void refreshProgress();
  }, [location.pathname, refreshProgress]);

  useEffect(() => {
    setDismissedThisView(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleRefresh = () => {
      void refreshProgress(true);
    };

    window.addEventListener('prompts-updated', handleRefresh);
    window.addEventListener('profile-updated', handleRefresh);
    window.addEventListener('portfolio-updated', handleRefresh);
    return () => {
      window.removeEventListener('prompts-updated', handleRefresh);
      window.removeEventListener('profile-updated', handleRefresh);
      window.removeEventListener('portfolio-updated', handleRefresh);
    };
  }, [refreshProgress]);

  useEffect(() => {
    const handlePromptProgress = (e: CustomEvent<{ progress: number; message?: string }>) => {
      if (location.pathname !== '/create-prompt') return;
      const { progress: draftProgress, message: draftMessage } = e.detail || {};
      if (typeof draftProgress === 'number') {
        setProgress(draftProgress);
      }
      if (draftMessage) {
        setMessage(customMessage || draftMessage);
      }
    };

    window.addEventListener('prompt-progress', handlePromptProgress as EventListener);
    return () =>
      window.removeEventListener('prompt-progress', handlePromptProgress as EventListener);
  }, [location.pathname, customMessage]);

  useEffect(() => {
    if (progress >= 100 && !hasShownRef.current && !userData?.onboardingCompleted) {
      setShowCongrats(true);
      hasShownRef.current = true;
    }
  }, [progress, userData]);

  const handleCongratsClose = async () => {
    try {
      await axiosInstance.get(API_ROUTES.USER.ONBOARD_COMPLETE);
      setShowCongrats(false);
      setUserData((prev) => (prev ? { ...prev, onboardingCompleted: true } : prev));
    } catch (err) {
      console.error('Error marking onboarding complete:', err);
    }
  };

  if (loading) return null;

  // Pre-dashboard setup hub has its own step UI; keep progress/confetti on create-prompt.
  if (location.pathname === '/get-started' || location.pathname.startsWith('/get-started/')) {
    return null;
  }

  const uid = userData?.id as number | string | undefined;
  const permanentlyDismissed =
    progress >= 100 && uid != null && isProfileCompleteBannerDismissed(uid);
  const hiddenForThisView = progress < 100 && dismissedThisView;

  if (permanentlyDismissed || hiddenForThisView || !message) return null;

  return (
    <div className="p-2" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <style>
        {`
          .progress-bar.bg-success { background-color: #2ecc71 !important; }
        `}
      </style>

      <ProgressBar
        now={progress}
        className="rounded-3"
        style={{ height: '8px' }}
        variant="success"
      />

      <div
        className="text-success small fw-semibold mt-1"
        style={{
          width: '100%',
          maxWidth: '100%',
          position: 'relative',
          paddingRight: '20px',
        }}
      >
        <span
          style={{
            display: 'block',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {progress}% — {message}
        </span>
        <button
          type="button"
          onClick={() => {
            const dismissUid =
              (userData?.id as number | string | undefined) ??
              (
                useSessionStore.getState().currentUser as {
                  id?: number;
                } | null
              )?.id;
            if (dismissUid == null) return;

            if (progress >= 100) {
              dismissProfileCompleteBanner(dismissUid);
            } else {
              setDismissedThisView(true);
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#27ae60',
            cursor: 'pointer',
            padding: '0 4px',
            fontSize: '16px',
            position: 'absolute',
            top: 0,
            right: 0,
            lineHeight: 1,
          }}
          title="Dismiss"
        >
          ✕
        </button>
      </div>

      {progress >= 100 && showCongrats && (
        <CongratsModal show={showCongrats} onClose={handleCongratsClose} />
      )}
    </div>
  );
};

export default OnboardingProgress;
